import { useState } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import {
  AlertOctagon,
  Ban,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  HelpCircle,
  Lock,
  Sparkles,
  Timer,
  Unlock,
  Wand2,
  X,
} from "lucide-react";
import type { AirportConfig, Disruption, ExtendResource, FlightLeg, PlacementStep, PredictedConflict, Turn } from "@yb/core";
import { api, type Board } from "../lib/api";
import { airlineColor, airlineName, delayMin, delayText, hhmm, label, legStatusText } from "../lib/format";

const MIN = 60_000;

function lead(min: number): string {
  if (min < 60) return `in ${min} min`;
  return `in ${Math.floor(min / 60)} h ${String(min % 60).padStart(2, "0")}`;
}

export function AlertsPanel({
  board,
  canEdit,
  onFocus,
  onFix,
}: {
  board: Board;
  canEdit: boolean;
  onFocus: (turnId: string, at: number) => void;
  onFix: (d: Disruption) => void;
}) {
  const [tab, setTab] = useState<"now" | "predicted">("now");
  const tz = board.airport.timezone;
  const hard = board.conflicts.filter((c) => c.severity === "hard");
  const warn = board.conflicts.filter((c) => c.severity === "warning");
  const upcoming = [...hard, ...warn];
  const turnById = new Map(board.turns.map((t) => [t.id, t]));

  const predictedFix = (p: PredictedConflict): Disruption | null => {
    const t = turnById.get(p.causeTurnId);
    const leg = t?.dep ?? t?.arr;
    if (!t || !leg) return null;
    const base = leg.estimated ?? leg.scheduled;
    return { kind: "delay", legId: leg.id, newTime: base + p.causeDelayMin * MIN };
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-1 border-b border-line px-3 pt-2">
        {(
          [
            ["now", `Now`, hard.length, "bad"],
            ["predicted", "Predicted", board.predictions.length, "warn"],
          ] as const
        ).map(([id, text, n, tone]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={clsx(
              "-mb-px flex items-center gap-1.5 border-b-2 px-2 pb-2 text-[13px] font-semibold",
              tab === id ? "border-ink text-ink" : "border-transparent text-ink-3 hover:text-ink-2",
            )}
          >
            {text}
            <span
              className={clsx(
                "tabular rounded-full px-1.5 text-[11px]",
                n === 0 ? "bg-panel-2 text-ink-3" : tone === "bad" ? "bg-bad text-white" : "bg-warn text-white",
              )}
            >
              {n}
            </span>
          </button>
        ))}
      </div>

      <div className="scroll-thin flex-1 overflow-auto p-2">
        {tab === "now" && (
          <>
            {hard.length > 0 && canEdit && (
              <button
                onClick={() => onFix({ kind: "resolve" })}
                className="mb-2 flex w-full items-center justify-center gap-2 rounded-lg bg-ink px-3 py-2 text-[13px] font-semibold text-panel hover:opacity-90"
              >
                <Wand2 className="size-4" /> Fix {hard.length} conflict{hard.length === 1 ? "" : "s"} — show options
              </button>
            )}
            {upcoming.length === 0 && (
              <Empty icon={<CheckCircle2 className="size-6 text-ok" />} title="No conflicts" text="Every turn has a stand that fits, with safe gaps." />
            )}
            {upcoming.map((c) => (
              <button
                key={c.id}
                onClick={() => onFocus(c.turnIds[0]!, c.at)}
                className="mb-1.5 flex w-full gap-2 rounded-lg border border-line bg-panel p-2 text-left hover:border-ink-3"
              >
                {c.severity === "hard" ? (
                  <AlertOctagon className="mt-0.5 size-4 shrink-0 text-bad" />
                ) : (
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
                )}
                <div className="min-w-0">
                  <div className="text-[12.5px] leading-snug">{c.message}</div>
                  <div className="tabular mt-0.5 text-[11px] text-ink-3">
                    {hhmm(c.at, tz)}
                    {c.at > board.now && ` · ${lead(Math.round((c.at - board.now) / MIN))}`}
                  </div>
                </div>
              </button>
            ))}
          </>
        )}

        {tab === "predicted" && (
          <>
            {board.predictions.length === 0 && (
              <Empty
                icon={<Sparkles className="size-6 text-ok" />}
                title="Nothing predicted"
                text="Live estimates and YLW delay history show no new conflicts in the next 4 hours."
              />
            )}
            {board.predictions.map((p) => {
              const fix = predictedFix(p);
              return (
                <div key={p.conflict.id} className="mb-1.5 rounded-lg border border-warn/50 bg-warn/5 p-2">
                  <button className="flex w-full gap-2 text-left" onClick={() => onFocus(p.causeTurnId, p.conflict.at)}>
                    <Clock3 className="mt-0.5 size-4 shrink-0 text-warn" />
                    <div className="min-w-0">
                      <div className="text-[12.5px] leading-snug">{p.conflict.message}</div>
                      <div className="mt-0.5 text-[11px] text-ink-3">
                        <span className="tabular font-semibold text-warn">{Math.round(p.probability * 100)}% likely</span> ·{" "}
                        {lead(p.leadMin)} · {p.causeDelayMin} min late ·{" "}
                        {p.basis === "feed-estimate" ? "Cirium estimate" : p.basis === "history" ? "YLW delay history" : p.basis}
                      </div>
                    </div>
                  </button>
                  {canEdit && fix && (
                    <button
                      onClick={() => onFix(fix)}
                      className="mt-2 w-full rounded-md border border-warn/60 px-2 py-1 text-[12px] font-semibold text-warn hover:bg-warn/10"
                    >
                      Plan around it now
                    </button>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function Empty({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="flex flex-col items-center px-4 py-10 text-center">
      {icon}
      <div className="mt-2 text-[13px] font-semibold">{title}</div>
      <div className="mt-1 text-[12px] text-ink-3">{text}</div>
    </div>
  );
}

function LegBlock({ leg, tz, extra }: { leg: FlightLeg; tz: string; extra?: React.ReactNode }) {
  const d = delayMin(leg);
  const arr = leg.direction === "ARR";
  return (
    <div className="rounded-lg border border-line p-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">
        {arr ? <ArrowDownRight className="size-3.5" /> : <ArrowUpRight className="size-3.5" />}
        {arr ? `Arrival from ${leg.otherAirport ?? "?"}` : `Departure to ${leg.otherAirport ?? "?"}`}
      </div>
      <div className="mt-1 flex items-baseline justify-between">
        <span className="text-[15px] font-bold">{leg.flightNumber}</span>
        <span className="text-[11px] text-ink-2">{legStatusText(leg)}</span>
      </div>
      <div className="tabular mt-1 grid grid-cols-3 gap-1 text-[11px]">
        <div>
          <div className="text-ink-3">Sched</div>
          <div className="font-semibold">{hhmm(leg.scheduled, tz)}</div>
        </div>
        <div>
          <div className="text-ink-3">Est</div>
          <div className="font-semibold">{hhmm(leg.estimated, tz)}</div>
        </div>
        <div>
          <div className="text-ink-3">Actual</div>
          <div className={clsx("font-semibold", d !== null && d > 5 && "text-bad")}>
            {hhmm(leg.actual, tz)} {delayText(d) && <span className="text-[10px]">{delayText(d)}</span>}
          </div>
        </div>
      </div>
      {(leg.pax != null || leg.bags != null || leg.screened != null) && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px]">
          {leg.pax != null && (
            <span>
              <b>{leg.pax}</b> pax
            </span>
          )}
          {leg.bags != null && (
            <span>
              <b>{leg.bags}</b> bags
            </span>
          )}
          {leg.screened != null && (
            <span className="text-info">
              <b>{leg.screened}</b> screened{leg.screenedQueued ? ` · ${leg.screenedQueued} in line` : ""}
            </span>
          )}
          {leg.loadSource && <span className="text-ink-3">from {leg.loadSource.replace(/^sheet:/, "sheet ").replace(/^email:/, "email ")}</span>}
        </div>
      )}
      {leg.codeshares && leg.codeshares.length > 0 && <div className="mt-1 text-[10.5px] text-ink-3">Codeshares {leg.codeshares.join(", ")}</div>}
      {extra}
    </div>
  );
}

export function FlightPanel({
  board,
  config,
  turn,
  canEdit,
  onClose,
  onMove,
  onLock,
  onDisrupt,
  onChangeType,
  onRemove,
  onRules,
  onExtend,
  onCancelFlight,
}: {
  board: Board;
  config: AirportConfig;
  turn: Turn;
  canEdit: boolean;
  onClose: () => void;
  onMove: (standId: string | null) => void;
  onLock: (locked: boolean) => void;
  onDisrupt: (d: Disruption) => void;
  /** The aircraft is not what the feed says. A real change, unlike the what-if. */
  onChangeType: (aircraftType: string) => void;
  /** Take the flight off the board, with a reason for the audit trail. */
  onRemove: (note: string) => void;
  /** Every rule the allocator uses, in one view. */
  onRules: () => void;
  /** Hold the stand, desks or a belt longer: opens the extend dialog. */
  onExtend?: (resource: ExtendResource) => void;
  /** Cancel the flight here: opens the dialog that asks about bags. */
  onCancelFlight?: () => void;
}) {
  const [removeReason, setRemoveReason] = useState<string | null>(null);
  const tz = board.airport.timezone;
  const a = board.plan.stands[turn.id];
  const stand = config.stands.find((s) => s.id === a?.standId);
  const explain = useQuery({
    queryKey: ["explain", board.date, turn.id, board.version.id, board.revision],
    queryFn: () => api.explain(board.date, turn.id),
  });
  const counters = turn.dep ? board.plan.counters[turn.dep.id] : undefined;
  const belt = turn.arr ? board.plan.belts[turn.arr.id] : undefined;
  const type = turn.aircraftType ? config.aircraftTypes[turn.aircraftType] : undefined;
  const [delayBy, setDelayBy] = useState(30);
  const mainLeg = turn.dep ?? turn.arr!;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start gap-2 border-b border-line p-3">
        <div className="mt-1 h-8 w-1.5 rounded-full" style={{ background: airlineColor(turn.airline, config) }} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[16px] font-bold">{label(turn)}</div>
          <div className="text-[12px] text-ink-2">
            {airlineName(turn.airline, config)} · {turn.registration ?? "no registration"} ·{" "}
            <span title={turn.aircraftTypeInferred ? "Not in the feed yet: assumed from the flight number" : undefined}>
              {type?.name ?? turn.aircraftType ?? "type unknown"}
              {turn.aircraftTypeInferred && <span className="ml-0.5 text-warn">(assumed)</span>}
            </span>
          </div>
        </div>
        <button onClick={onClose} className="rounded p-1 text-ink-3 hover:bg-panel-2" aria-label="Close">
          <X className="size-4" />
        </button>
      </div>

      <div className="scroll-thin flex-1 space-y-3 overflow-auto p-3">
        <div className="flex items-center gap-3 rounded-lg bg-panel-2 p-2.5">
          <div className="flex-1">
            <div className="text-[11px] text-ink-3">Stand</div>
            <div className="text-[18px] font-bold">{stand?.name ?? "Unallocated"}</div>
            <div className="tabular text-[11px] text-ink-2">
              {stand?.gate ?? ""} · {hhmm(turn.start, tz)}–{hhmm(turn.end, tz)}
              {turn.deiceMin ? <span className="ml-1 text-ink-3">incl. {turn.deiceMin} min de-icing</span> : null}
              {turn.segment && <span className="ml-1 text-ink-3">({turn.segment === "arrival" ? "then towed off" : "towed in"})</span>}
            </div>
          </div>
          {canEdit && (
            <button
              onClick={() => onLock(!a?.locked)}
              className={clsx(
                "flex min-h-9 items-center gap-1 rounded-md border px-2.5 py-1 text-[12px] font-semibold",
                a?.locked ? "border-ink bg-ink text-panel" : "border-line text-ink-2 hover:border-ink-3",
              )}
              title="Locked turns are never moved by the optimizer"
            >
              {a?.locked ? <Lock className="size-3.5" /> : <Unlock className="size-3.5" />}
              {a?.locked ? "Locked" : "Lock"}
            </button>
          )}
        </div>

        {canEdit && (onExtend || onCancelFlight) && (
          <div className="flex flex-wrap gap-1.5">
            {onExtend && (
              <ActionButton onClick={() => onExtend("stand")}>
                <Timer className="size-3.5" /> Extend stand
              </ActionButton>
            )}
            {onExtend && turn.dep && counters && (
              <ActionButton onClick={() => onExtend("counters")}>
                <Timer className="size-3.5" /> Extend check-in
              </ActionButton>
            )}
            {onExtend && turn.arr && belt && (
              <ActionButton onClick={() => onExtend("belts")}>
                <Timer className="size-3.5" /> Extend reclaim
              </ActionButton>
            )}
            {onCancelFlight && (
              <ActionButton onClick={onCancelFlight} tone="bad">
                <Ban className="size-3.5" /> Cancel flight
              </ActionButton>
            )}
          </div>
        )}

        {turn.arr && (
          <LegBlock
            leg={turn.arr}
            tz={tz}
            extra={belt && <div className="mt-1 text-[11px] text-ink-2">Bags: {config.belts.find((b) => b.id === belt.beltId)?.name ?? "no belt"}</div>}
          />
        )}
        {turn.dep && (
          <LegBlock
            leg={turn.dep}
            tz={tz}
            extra={
              counters && (
                <div className="mt-1 text-[11px] text-ink-2">
                  Check-in: counters{" "}
                  {counters.counters.map((c) => config.counters.find((x) => x.id === c)?.number).join(", ") || "none"} ·{" "}
                  {hhmm(counters.from, tz)}–{hhmm(counters.to, tz)}
                </div>
              )
            }
          />
        )}

        <div className="rounded-lg border border-line p-2.5">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold">
            <HelpCircle className="size-4 text-info" /> Why this stand?
          </div>
          {explain.isLoading && <div className="mt-2 text-[12px] text-ink-3">Working it out…</div>}
          {explain.data && (
            <>
              <ul className="mt-1.5 space-y-1 text-[12px] text-ink-2">
                {explain.data.reasons.map((r, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-ink-3">•</span>
                    {r}
                  </li>
                ))}
              </ul>
              {explain.data.standId && <RulesWeighed weighed={explain.data.weighed ?? []} />}
              {!!explain.data.history?.length && <PlacementHistory steps={explain.data.history} tz={tz} />}
              {!!explain.data.autoFix?.length && (
                <>
                  <div className="mt-2.5 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Why the app has not moved it</div>
                  <ul className="mt-1 space-y-1 text-[12px] text-ink-2">
                    {explain.data.autoFix.map((r, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="text-ink-3">•</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div className="mt-2.5 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Alternatives</div>
              <div className="mt-1 space-y-1">
                {explain.data.alternatives.slice(0, 7).map((alt) => (
                  <div key={alt.standId} className="flex items-center gap-2 text-[12px]">
                    <span className={clsx("w-16 shrink-0 font-semibold", !alt.available && "text-ink-3")}>{alt.standName}</span>
                    <span className={clsx("min-w-0 flex-1 truncate", alt.available ? "text-ink-2" : "text-ink-3")} title={alt.why}>
                      {alt.why}
                    </span>
                    {alt.available && alt.extraCost !== 0 && (
                      <span
                        className={clsx("tabular shrink-0 text-[10.5px]", alt.extraCost < 0 ? "text-ok" : "text-ink-3")}
                        title="Points against it compared with the stand it has: the optimizer picks the fewest"
                      >
                        {alt.extraCost > 0 ? `+${alt.extraCost}` : alt.extraCost}
                      </span>
                    )}
                    {canEdit && alt.available && (
                      <button
                        onClick={() => onMove(alt.standId)}
                        className="min-h-7 shrink-0 rounded border border-line px-2 py-0.5 text-[11px] font-semibold hover:border-ink-3"
                      >
                        Move
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {canEdit && (
          <div className="rounded-lg border border-line p-2.5">
            <div className="text-[12px] font-semibold">What if…</div>
            <div className="mt-2 flex items-center gap-2">
              <select
                className="rounded-md border border-line bg-panel px-1.5 py-1 text-[12px]"
                value={delayBy}
                onChange={(e) => setDelayBy(Number(e.target.value))}
              >
                {[15, 30, 45, 60, 90, 120].map((m) => (
                  <option key={m} value={m}>
                    +{m} min
                  </option>
                ))}
              </select>
              <button
                onClick={() => onDisrupt({ kind: "delay", legId: mainLeg.id, newTime: (mainLeg.estimated ?? mainLeg.scheduled) + delayBy * MIN })}
                className="flex-1 rounded-md border border-line px-2 py-1 text-[12px] font-semibold hover:border-ink-3"
              >
                {mainLeg.flightNumber} is late
              </button>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                onClick={() => onDisrupt({ kind: "cancel", legId: mainLeg.id })}
                className="rounded-md border border-line px-2 py-1 text-[12px] font-semibold hover:border-ink-3"
              >
                Cancel {mainLeg.flightNumber}
              </button>
              <select
                className="rounded-md border border-line bg-panel px-1.5 py-1 text-[12px] font-semibold"
                value=""
                onChange={(e) => e.target.value && onDisrupt({ kind: "type-change", legId: mainLeg.id, aircraftType: e.target.value })}
              >
                <option value="">If it were a…</option>
                {Object.values(config.aircraftTypes)
                  .filter((t) => t.code !== turn.aircraftType)
                  .sort((x, y) => x.code.localeCompare(y.code))
                  .map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.code} · {t.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        )}

        {canEdit && (
          <div className="rounded-lg border border-line p-2.5">
            <div className="text-[12px] font-semibold">Correct the feed</div>
            <p className="mt-0.5 text-[11px] text-ink-3">These hold, unlike a what-if. A removed flight is put back from History; a type correction comes off from the bar's right-click menu.</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <select
                className="rounded-md border border-line bg-panel px-1.5 py-1 text-[12px] font-semibold"
                value=""
                onChange={(e) => e.target.value && onChangeType(e.target.value)}
                title="The feed has the aircraft type wrong"
              >
                <option value="">Aircraft is really a…</option>
                {Object.values(config.aircraftTypes)
                  .filter((t) => t.code !== turn.aircraftType)
                  .sort((x, y) => x.code.localeCompare(y.code))
                  .map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.code} · {t.name}
                    </option>
                  ))}
              </select>
              {removeReason === null ? (
                <button
                  onClick={() => setRemoveReason("Duplicate in the feed")}
                  className="rounded-md border border-line px-2 py-1 text-[12px] font-semibold text-bad hover:border-bad"
                  title="Take this flight off the board, whatever the feed keeps saying"
                >
                  Remove from board
                </button>
              ) : (
                <div className="col-span-2 flex items-center gap-1.5">
                  <input
                    className="min-w-0 flex-1 rounded-md border border-line bg-panel px-1.5 py-1 text-[12px]"
                    value={removeReason}
                    maxLength={200}
                    autoFocus
                    onChange={(e) => setRemoveReason(e.target.value)}
                    placeholder="Why it is coming off"
                  />
                  <button
                    onClick={() => {
                      onRemove(removeReason.trim());
                      setRemoveReason(null);
                    }}
                    disabled={!removeReason.trim()}
                    className="rounded-md bg-bad px-2 py-1 text-[12px] font-bold text-panel disabled:opacity-50"
                  >
                    Remove
                  </button>
                  <button onClick={() => setRemoveReason(null)} className="rounded-md border border-line px-2 py-1 text-[12px]">
                    Keep
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-[11px]">
          <button className="text-info hover:underline" onClick={onRules}>
            Every rule the allocator uses, in one view
          </button>
        </div>

        <div className="text-[11px] text-ink-3">
          Linked by {turn.linkedBy === "none" ? "nothing (one-sided turn, ground time assumed)" : turn.linkedBy.replace("-", " ")} · source{" "}
          {mainLeg.source}
        </div>
      </div>
    </div>
  );
}

/**
 * The optimizer's arithmetic for the stand a flight has: every rule that counted against it,
 * with its points. A duty manager reading "why Stand 10 and not a bridge" sees the jet
 * penalty and what outweighed it, rather than taking the choice on trust.
 */
export function RulesWeighed({ weighed }: { weighed: Array<{ code: string; text: string; cost: number }> }) {
  const total = weighed.reduce((n, w) => n + w.cost, 0);
  return (
    <div className="mt-2.5">
      <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Rules weighed</div>
      {weighed.length === 0 ? (
        <div className="mt-1 text-[12px] text-ink-3">No rule counts against this stand.</div>
      ) : (
        <table className="mt-1 w-full text-[12px]">
          <tbody>
            {weighed.map((w, i) => (
              <tr key={i} className="align-top">
                <td className="py-0.5 pr-2 text-ink-2">{w.text}</td>
                <td className="tabular py-0.5 text-right text-ink-3">{w.cost}</td>
              </tr>
            ))}
            <tr className="border-t border-line font-semibold">
              <td className="pt-1 pr-2">Points against this stand</td>
              <td className="tabular pt-1 text-right">{total}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

const KIND_LABEL: Record<string, string> = {
  import: "First plan",
  optimize: "Optimized",
  manual: "Planner",
  recovery: "Recovery",
  auto: "App",
  sms: "Approved by text",
  undo: "Undo",
};

/** How the flight came to be on its stand: each plan change that moved it, oldest first. */
function PlacementHistory({ steps, tz }: { steps: PlacementStep[]; tz: string }) {
  return (
    <>
      <div className="mt-2.5 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">How it got here</div>
      <ol className="mt-1 space-y-1 text-[12px]">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-2">
            <span className="tabular w-11 shrink-0 text-ink-3">{hhmm(Date.parse(s.at), tz)}</span>
            <span className="min-w-0 flex-1">
              <b>{s.standName}</b>
              <span className="text-ink-3">
                {" "}
                · {KIND_LABEL[s.kind] ?? s.kind}
                {s.by ? ` (${s.by})` : ""}
              </span>
              {s.summary && <div className="truncate text-[11px] text-ink-3" title={s.summary}>{s.summary}</div>}
            </span>
          </li>
        ))}
      </ol>
    </>
  );
}

/** A panel action big enough for a finger. */
function ActionButton({ onClick, tone, children }: { onClick: () => void; tone?: "bad"; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "flex min-h-9 items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12.5px] font-semibold",
        tone === "bad" ? "border-bad/40 text-bad hover:border-bad" : "border-line text-ink-2 hover:border-ink-3",
      )}
    >
      {children}
    </button>
  );
}
